/**
 * 7레이어 목록 (DESIGN §8.2.1).
 *
 * **꺼진 레이어를 목록에서 숨기지 않는다** (규칙 3). 사라진 레이어는 "없는 기능"과
 * 구별되지 않는다. 비활성인 것은 어느 게이트·어느 파일 때문인지 함께 적는다.
 *
 * 레이어 1·2 는 서로 배타적이므로 켜고 끄는 대상이 아니라 **줌이 정하는 상태**다 —
 * 체크박스로 두면 둘을 동시에 켤 수 있게 되어 §8.2.1 규칙 2 를 깬다.
 */

import { disabledReason } from '@/lib/client'
import type { ClientConfig } from '@/lib/types'
import './LayerPanel.css'

export interface LayerState {
  /** 현재 표현 (레이어 1·2 중 무엇이 살아 있는가) */
  representation: string
}

interface Row {
  n: number
  name: string
  /** 이 레이어를 여는 조건. null 이면 항상 가능 */
  feature: string | null
  /** 게이트와 무관하게 아직 구현되지 않은 레이어 */
  todo?: string
  note: string
}

const LAYERS: Row[] = [
  { n: 1, name: '충전기 포인트', feature: null, note: 'zoom ≥ 11 에서 자동' },
  { n: 2, name: 'H3 육각 집계', feature: null, note: 'zoom < 11 에서 자동 (r7 / r9)' },
  { n: 3, name: '커버리지 표시', feature: null, note: '값 없는 셀은 회색 — 항상 적용' },
  { n: 4, name: '시군구 코로플레스', feature: 'choropleth', note: '경계 GeoJSON 필요' },
  {
    n: 5,
    name: 'ASOS 관측소',
    feature: 'weather',
    todo: '관측소 좌표 파일 대기',
    note: 'asos_stations.csv 필요',
  },
  { n: 6, name: '기상 IDW 격자', feature: 'weather', todo: 'ASOS 백필 후', note: '일자료 수집 필요' },
  { n: 7, name: '이상 급증 마커', feature: 'changepoint', note: 'STL 잔차 robust z > 3.5' },
]

export function LayerPanel({
  config,
  representation,
}: {
  config: ClientConfig | undefined
  representation: string
}) {
  const activeByZoom = (n: number) =>
    (n === 1 && representation === 'points') || (n === 2 && representation.startsWith('h3'))

  return (
    <div className="lpanel">
      <h3 className="lpanel__title">레이어</h3>
      <ul className="lpanel__list">
        {LAYERS.map((l) => {
          const gated = l.feature ? disabledReason(config, l.feature) : null
          const off = gated != null || l.todo != null
          const on = !off && (l.n <= 3 ? l.n === 3 || activeByZoom(l.n) : false)
          return (
            <li key={l.n} className={`lpanel__row${off ? ' is-off' : ''}${on ? ' is-on' : ''}`}>
              <span className="lpanel__n num" aria-hidden="true">
                {l.n}
              </span>
              <span className="lpanel__body">
                <span className="lpanel__name">
                  {l.name}
                  {on && <span className="lpanel__badge">표시 중</span>}
                </span>
                <span className="lpanel__why">{gated ?? l.todo ?? l.note}</span>
              </span>
            </li>
          )
        })}
      </ul>
      {config && config.missing_reference_files.length > 0 && (
        <div className="lpanel__need">
          <strong>필요한 파일</strong>
          <ul>
            {config.missing_reference_files.map((m) => (
              <li key={m.path}>
                <code>data/ref/{m.path}</code> — {m.blocks}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
